import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LockSimple, Paperclip, ArrowBendUpLeft, CalendarCheck, NotePencil, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import { KIND, DIRECTION, TONE, Pill, fmtDateTime, counterpart } from "./commMeta";

function Meta({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-xs py-1 border-b border-slate-100">
      <span className="text-slate-500">{label}</span><span className="text-slate-800 break-words">{children}</span>
    </div>
  );
}

function FollowUpBox({ e, reload }) {
  const [date, setDate] = useState("");
  const fu = e.follow_up;
  const set = async () => {
    try { await api.post(`/communications/${e.id}/follow-up`, { due_date: date }); toast.success("Wiedervorlage angelegt"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const done = async () => { await api.post(`/communications/${e.id}/follow-up/done`); toast.success("Wiedervorlage erledigt"); reload(); };
  if (fu?.status === "open") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="comm-followup-open">
        <CalendarCheck size={14} /> Wiedervorlage bis {fu.due_date}{fu.note ? ` · ${fu.note}` : ""}
        <Button size="sm" variant="outline" className="ml-auto h-6 text-xs" onClick={done} data-testid="comm-followup-done">Erledigt</Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      {fu?.status === "done" && <span className="text-emerald-700">Wiedervorlage erledigt{fu.done_by_reply_id ? " (durch Antwort)" : ""}</span>}
      <Input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} className="h-7 w-36 text-xs ml-auto" data-testid="comm-followup-date" />
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!date} onClick={set} data-testid="comm-followup-set">Wiedervorlage setzen</Button>
    </div>
  );
}

export default function CommunicationDetail({ id, onClose, onReplyIncoming, onReplyLetter, onChanged }) {
  const [e, setE] = useState(null);
  const [note, setNote] = useState("");
  const [docId, setDocId] = useState(null);
  const load = useCallback(() => { if (id) api.get(`/communications/${id}`).then(({ data }) => setE(data)); }, [id]);
  useEffect(() => { setE(null); load(); }, [load]);
  const reload = () => { load(); onChanged?.(); };

  const addNote = async () => {
    await api.post(`/communications/${id}/addenda`, { text: note });
    setNote(""); toast.success("Vermerk hinzugefügt"); reload();
  };

  const K = KIND[e?.kind] || KIND.note;
  const D = DIRECTION[e?.direction] || DIRECTION.internal;
  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="comm-detail">
        {!e ? <div className="text-sm text-slate-500 p-4">Lade…</div> : (
          <div className="space-y-4">
            <SheetHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <Pill cls={D.cls}><D.icon size={10} /> {D.label}</Pill>
                <Pill cls="text-slate-700 bg-white border-slate-200"><K.icon size={10} /> {K.label}</Pill>
                <Pill cls={TONE[e.status?.tone] || TONE.neutral} testId="comm-detail-status">{e.status?.label}</Pill>
                {e.batch_mode && <Pill cls="text-violet-700 bg-violet-50 border-violet-200">{e.batch_mode === "serial" ? "Serienbrief" : "Rundschreiben"}</Pill>}
              </div>
              <SheetTitle className="text-left" data-testid="comm-detail-subject">{e.subject}</SheetTitle>
            </SheetHeader>
            <div>
              <Meta label="Datum / Uhrzeit">{fmtDateTime(e.occurred_at)}</Meta>
              <Meta label={e.direction === "in" ? "Absender" : "Empfänger"}>
                {e.direction === "in" ? `${counterpart(e)}${e.sender?.email ? ` <${e.sender.email}>` : ""}`
                  : (e.recipients || []).map((r) => `${r.name || "—"}${r.email ? ` <${r.email}>` : ""}${r.address ? ` · ${r.address}` : ""}`).join("; ") || "—"}
              </Meta>
              <Meta label="Verfasser">{e.author_name}</Meta>
              <Meta label="Versandweg">{(e.channels || []).map((c) => ({ email: "E-Mail", post: "Post" }[c] || c)).join(", ") || "—"}</Meta>
              <Meta label="Erfasst am">{fmtDateTime(e.created_at)}{e.source === "backfill" ? " (aus Altbestand übernommen)" : ""}</Meta>
              {e.sha256 && <Meta label="Prüfsumme"><span className="font-mono text-[10px]">SHA-256 {e.sha256}</span></Meta>}
            </div>
            {e.locked && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="comm-detail-locked">
                <LockSimple size={12} /> Revisionssicher archiviert — Inhalt unveränderbar, Ergänzungen nur als Vermerk.
              </div>
            )}
            {(e.document || e.attachments?.length > 0) && (
              <div className="space-y-1">
                {e.document && (
                  <button type="button" onClick={() => setDocId(e.document.id)} className="flex items-center gap-1.5 text-sm text-slate-800 hover:underline" data-testid="comm-detail-document">
                    <FileText size={14} /> {e.document.original_filename || e.document.title}
                  </button>
                )}
                {e.attachments.map((a) => (
                  <button key={a.id} type="button" onClick={() => setDocId(a.id)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:underline pl-1" data-testid={`comm-detail-att-${a.id}`}>
                    <Paperclip size={12} /> {a.title || a.original_filename}
                  </button>
                ))}
              </div>
            )}
            {(e.content_html || e.content_text) && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm max-h-80 overflow-y-auto" data-testid="comm-detail-content">
                {e.content_html ? <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: e.content_html }} />
                  : <div className="whitespace-pre-wrap">{e.content_text}</div>}
              </div>
            )}
            {(e.parent || e.replies?.length > 0) && (
              <div className="text-xs space-y-1">
                {e.parent && <div>Antwort auf: <b>{e.parent.subject}</b> ({fmtDateTime(e.parent.occurred_at)})</div>}
                {e.replies.map((r) => <div key={r.id}>Antwort erhalten: <b>{r.subject}</b> ({fmtDateTime(r.occurred_at)})</div>)}
              </div>
            )}
            <FollowUpBox e={e} reload={reload} />
            <div className="flex flex-wrap gap-2">
              {e.direction === "out" && <Button size="sm" variant="outline" className="gap-1" onClick={() => onReplyIncoming(e)} data-testid="comm-detail-reply-in"><ArrowBendUpLeft size={14} /> Antwort erfassen</Button>}
              <Button size="sm" variant="outline" className="gap-1" onClick={() => onReplyLetter(e)} data-testid="comm-detail-reply-letter"><NotePencil size={14} /> {e.direction === "in" ? "Beantworten" : "Nachfassen"}</Button>
            </div>
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Vermerke</div>
              {(e.addenda || []).map((a) => (
                <div key={a.id} className="text-xs border-l-2 border-amber-300 pl-2"><span className="text-slate-500">{fmtDateTime(a.at)} · {a.user_name}:</span> {a.text}</div>
              ))}
              <div className="flex gap-2">
                <Textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Vermerk / Nachtrag…" className="text-xs" data-testid="comm-addendum-input" />
                <Button size="sm" disabled={!note.trim()} onClick={addNote} data-testid="comm-addendum-save">Speichern</Button>
              </div>
            </div>
          </div>
        )}
        {docId && <DocumentPreviewDrawer docId={docId} onClose={() => setDocId(null)} />}
      </SheetContent>
    </Sheet>
  );
}
